import { useEffect, useState } from 'react';
import { Account, PortalUserData, UserRoles, UserTeam } from '../../../../types';
import { getAccountSubject } from '../../../../store/Accounts';
import { getRoles, getRoleSubject, SKIP_ACCOUNT_ROLES, useRoles } from '../../../../store/Role';
import { formatSupportCaseDate } from '../../../../utils/helpers';
import { RoleSelectionForm, TeamSelectionForm } from '../../../Compounds/UserCreation';
import { DetailViewCard } from '../../../Compounds/CardWithTitle';
import {
  getPortalUserUpdateSubject,
  updatePortalUser,
  getPortalUsersLoadingSubject
} from '../../../../store/PortalUser';
import { isAdmin, isSupport, managesTeam, getUser } from '../../../../store/User';
import { Team, useTeamsStore, getTeamsSubject } from '../../../../store/Team';
import { ComponentSpinner } from '../../../Compounds/Loading/ComponentSpinner';
import { getAcctCustByAccountId } from '../../../../store/AccountCustomization';
import { combineLatest } from 'rxjs';
import { Switch } from '@mui/material';
import { useAdviseTeam } from '../../../Compounds/UserCreation/useAdviseTeam';
import { missingAdviseTeam } from '../../../../utils/adviseRoles';
import { DetailRow, TextInputRow } from './DetailRow';
import { DetailRow as ValueRow } from '../../../Compounds/DetailsDrawer/DetailRow';
import { SubAccountsRow } from './SubAccountsRow';
import { PrimaryAccountRow } from './PrimaryAccountRow';

export const EditUserForm = () => {
  const { rolesLoading } = useRoles();
  const teamManager = managesTeam(getUser());
  const admin = isAdmin(getUser());
  const support = isSupport(getUser());
  const [userData, setUserData] = useState<PortalUserData>();
  const [roles, setRoles] = useState<UserRoles[]>(getRoles());
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>();
  const [displayAccounts, setDisplayAccounts] = useState<Account[]>([]);
  const [secondaryAccounts, setSecondaryAccounts] = useState<Account[]>([]);
  const [selectedSecondaryAccounts, setSelectedSecondaryAccounts] = useState<Account[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [portalUsersLoading, setPortalUsersLoading] = useState<boolean>(false);
  const [displayAccount, setDisplayAccount] = useState<boolean>(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [accountTeams, setAccountTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<UserTeam | null>(null);
  const { fetchTeams } = useTeamsStore();

  const handleAccountChange = (value: Account | undefined) => {
    if (value) {
      const updatedUserData = {
        ...userData,
        account_id: value.id,
        account_name: value.name
      };
      updatePortalUser(updatedUserData as PortalUserData);
    }

    setSelectedAccount(value);
  };

  const handleRolesUpdate = (selected: string[]) => {
    const filteredRoles = roles.filter((role) => selected.includes(role.name || ''));
    setSelectedRoles(filteredRoles.map((role) => role.name).filter((name): name is string => name !== undefined));
    const hidesAccount = selected.some((role) => SKIP_ACCOUNT_ROLES.includes(role));
    const updatedUserData = {
      ...userData,
      roles: filteredRoles,
      ...(hidesAccount && { account_id: undefined, account_name: undefined })
    };
    updatePortalUser(updatedUserData as PortalUserData);
    if (hidesAccount) setSelectedAccount(undefined);
  };

  const handleChange = (name: keyof PortalUserData, value: string) => {
    const updatedUserData = {
      ...userData,
      [name]: value.trim()
    };

    updatePortalUser(updatedUserData as PortalUserData);
  };

  const handleTwoFactorAuthChange = () => {
    const updatedUserData = {
      ...userData,
      otp_required_for_login: !(userData?.otp_required_for_login === true)
    };
    updatePortalUser(updatedUserData as PortalUserData);
  };

  const handleTeamChange = (value: UserTeam | null) => {
    const updatedUserData = {
      ...userData,
      team_id: value ? Number(value.id) : null,
      team_name: value?.name
    };
    updatePortalUser(updatedUserData as PortalUserData);
    setSelectedTeam(value);
  };

  const adviseTeam = useAdviseTeam(selectedRoles, teams, selectedTeam, () => handleTeamChange(null));
  const teamMissing = missingAdviseTeam(selectedRoles, userData?.team_id);

  const handleSecondaryAccountsSelect = (value: Account[] | null) => {
    const uniqueAccounts = (value || [])
      .filter((account, index, self) => index === self.findIndex((a) => a.id === account.id))
      .filter((acct) => acct.id !== userData?.account_id); // Exclude primary account

    setSelectedSecondaryAccounts(uniqueAccounts);
    updatePortalUser({ ...userData, accounts: uniqueAccounts } as PortalUserData);
  };

  useEffect(() => {
    const dataSubscription = combineLatest([
      getTeamsSubject(),
      getRoleSubject(),
      getAccountSubject(),
      getPortalUsersLoadingSubject(),
      getPortalUserUpdateSubject()
    ]).subscribe(([teams, roles, accounts, usersLoading, userUpdate]) => {
      setTeams(teams);
      setRoles(roles);
      setDisplayAccounts(accounts.sort((a, b) => (a.name > b.name ? 1 : -1)));
      setPortalUsersLoading(usersLoading);
      setUserData(userUpdate);

      if (userUpdate?.account_id && userUpdate?.account_name) {
        setSelectedAccount({ id: userUpdate.account_id, name: userUpdate.account_name });
        const acctTeams = selectedAccount?.id ? teams.filter((t) => t.account_id === userUpdate?.account_id) : teams;
        setAccountTeams(acctTeams);
      }
    });

    fetchTeams();

    return () => {
      if (dataSubscription) dataSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const acctTeams = selectedAccount?.id ? teams.filter((t) => t.account_id === selectedAccount?.id) : teams;
    setAccountTeams(acctTeams);

    const subAccounts = getAcctCustByAccountId(selectedAccount?.id)?.content?.sub_accounts || [];
    setSecondaryAccounts(subAccounts);
  }, [selectedAccount, accountTeams.length]);

  useEffect(() => {
    const currentRoles = userData?.roles
      ?.map((role) => role?.name)
      .filter((name): name is string => name !== undefined);
    setSelectedRoles(currentRoles || []);
  }, [userData?.roles, userData?.id, userData?.roles?.length]);

  useEffect(() => {
    const hide = selectedRoles.some((role) => SKIP_ACCOUNT_ROLES.includes(role));
    setDisplayAccount(!hide && !teamManager);
  }, [selectedRoles.join('|')]);

  useEffect(() => {
    if (userData && userData.account_id) {
      setSelectedAccount({ id: userData.account_id, name: userData.account_name } as Account);
    } else {
      setSelectedAccount(undefined);
    }

    if (userData && !!userData?.accounts?.length) {
      setSelectedSecondaryAccounts(userData.accounts.filter((acct) => acct.id !== userData.account_id)); // Exclude primary account
    } else {
      setSelectedSecondaryAccounts([]);
    }
  }, [userData?.account_id, userData?.id]);

  useEffect(() => {
    const team = userData?.team_id ? teams.find((t) => Number(t.id) === userData.team_id) : undefined;
    setSelectedTeam(team ? { id: team.id, name: team.name } : null);
  }, [userData?.team_id, userData?.id, teams]);

  if (portalUsersLoading) return <ComponentSpinner />;

  return (
    <>
      <DetailViewCard
        key="admin"
        title="Details">
        <form onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
          <ValueRow
            testId="detail-row-id"
            title="User ID"
            value={String(userData?.id ?? '')}
            colId="id"
            labelWidth="w-3/5"
          />
          <TextInputRow
            field="first_name"
            label="First Name"
            inputId="firstName"
            dataCy="first-name"
            value={userData?.first_name || ''}
            onChange={handleChange}
          />
          <TextInputRow
            field="last_name"
            label="Last Name"
            inputId="lastName"
            dataCy="last-name"
            value={userData?.last_name || ''}
            onChange={handleChange}
          />
          <TextInputRow
            field="email"
            label="Email"
            inputId="email"
            dataCy="email"
            value={userData?.email || ''}
            onChange={handleChange}
          />
          {displayAccount && (
            <PrimaryAccountRow
              accounts={displayAccounts}
              selectedAccount={selectedAccount}
              onSelect={handleAccountChange}
            />
          )}
          {!!secondaryAccounts.length && (
            <SubAccountsRow
              subAccounts={secondaryAccounts}
              selectedAccounts={selectedSecondaryAccounts}
              editable={teamManager || admin || support}
              onSelect={handleSecondaryAccountsSelect}
            />
          )}
          <ValueRow
            testId="detail-row-updated_at"
            title="Last Login"
            value={formatSupportCaseDate(userData?.updated_at || '')}
            colId="updated_at"
            labelWidth="w-3/5"
          />
          <DetailRow
            testId="detail-row-two-factor-authentication"
            label="Two-Factor Authentication">
            <div className="w-full">
              <Switch
                sx={{
                  '.Mui-checked': {
                    color: '#3B6FE8 !important'
                  },
                  '.MuiSwitch-track': {
                    backgroundColor: '#878EBE !important'
                  }
                }}
                size="small"
                checked={userData?.otp_required_for_login === true}
                onChange={handleTwoFactorAuthChange}
              />
            </div>
          </DetailRow>
          {!teamManager && (displayAccount || adviseTeam.active) && (
            <DetailRow
              testId="detail-row-team"
              label="Team">
              <TeamSelectionForm
                selectedTeam={selectedTeam}
                setSelectedTeam={handleTeamChange}
                size="small"
                styles="w-full"
                teams={adviseTeam.active ? adviseTeam.teams : accountTeams}
                required={adviseTeam.active}
                helperText={adviseTeam.helperText ?? (teamMissing ? 'Pick a team before saving.' : undefined)}
              />
            </DetailRow>
          )}
        </form>
      </DetailViewCard>
      <DetailViewCard
        key="admin_roles"
        title="Roles">
        {rolesLoading ? (
          <ComponentSpinner />
        ) : (
          <RoleSelectionForm
            selectedRoles={selectedRoles}
            updateRoles={handleRolesUpdate}
            drawer={true}
            size="small"
          />
        )}
      </DetailViewCard>
    </>
  );
};
